"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";

declare global {
  interface Window {
    markerConfig: {
      project: string;
      source: string;
    };
    __Marker: any;
    Marker: any;
  }
}

// Routes where Marker.io should be hidden
const HIDDEN_PATHS = ["/bookings", "/customerportal"];

// Helper to check if current path should show Marker.io
const shouldShowMarker = (pathname: string | null) =>
  !HIDDEN_PATHS.some((path) => pathname?.startsWith(path));

export default function MarkerIO() {
  const pathname = usePathname();
  const scriptLoaded = useRef(false);

  // Only load and show Marker.io on allowed paths
  useEffect(() => {
    const showMarker = shouldShowMarker(pathname);

    // If on hidden path, hide widget and don't load script
    if (!showMarker) {
      if (window.Marker) {
        window.Marker.hide();
      }
      return;
    }

    // If already loaded, just show it
    if (scriptLoaded.current && window.Marker) {
      window.Marker.show();
      return;
    }

    // Don't initialize twice
    if (scriptLoaded.current) return;
    scriptLoaded.current = true;

    // Set the marker configuration
    window.markerConfig = {
      project: "68bedd3d0ee9ff7d817b9372",
      source: "snippet",
    };

    // Initialize Marker.io
    if (!window.__Marker) {
      window.__Marker = {};
      const tasks: any[] = [];
      const marker: any = { __cs: tasks };

      [
        "show",
        "hide",
        "isVisible",
        "capture",
        "cancelCapture",
        "unload",
        "reload",
        "isExtensionInstalled",
        "setReporter",
        "clearReporter",
        "setCustomData",
        "on",
        "off",
      ].forEach(function (method) {
        marker[method] = function () {
          const args = Array.prototype.slice.call(arguments);
          args.unshift(method);
          tasks.push(args);
        };
      });

      window.Marker = marker;

      // Load the Marker.io script
      const script = document.createElement("script");
      script.async = true;
      script.src = "https://edge.marker.io/latest/shim.js";
      const firstScript = document.getElementsByTagName("script")[0];
      if (firstScript && firstScript.parentNode) {
        firstScript.parentNode.insertBefore(script, firstScript);
      }
    }
  }, [pathname]);

  return null;
}
